/// <amd-module name="Repos/Discussion/Editor/Widgets/AddCommentWidget" />
import * as React from "react";
import * as ReactDOM from "react-dom";
import { IDiffDiscussionWidget, IDiffMonacoDiscussionWidget, ILineInfo, LineInfoType } from "Repos/Discussion/Common/DiscussionContracts";
import * as Resources from "Repos/Discussion/Editor/Resources";
import { AddCommentButton } from "../Components/AddCommentButton";
import { AddBaseWidget } from "./AddBaseWidget";

export const EmptyDiffDiscussionWidget: IDiffDiscussionWidget = {
    belongsToLine: () => false,
    containsThread: () => false,
    removeThread: () => {},
    addThread: () => {},
    getThreadCount: () => 0,
    hasThreads: () => false,
    isAttachedToFile: () => false,
    getLineNumber: () => -1
};

export interface IAddCommentWidgetProps {
    addToOriginalDiff: boolean;
    onAddDiscussion: (lineInfo: ILineInfo, addToOriginalDiff: boolean) => void;
}
/**
 * Gets a content widget used to start a discusson thread for the selected text.
 */
export function getAddCommentRegistration(props: IAddCommentWidgetProps): IDiffMonacoDiscussionWidget<AddCommentWidget> {
    const { addToOriginalDiff } = props;
    return {
        ...EmptyDiffDiscussionWidget,
        addToOriginalDiff,
        addWidget: (editor, diffEditor?) => {
            if (diffEditor) {
                const isOriginalDiff = diffEditor.getOriginalEditor() === editor;
                if (isOriginalDiff !== addToOriginalDiff) {
                    return undefined;
                }
            }

            const widget = new AddCommentWidget(editor, props);
            editor.addContentWidget(widget);
            return widget;
        },
        removeWidget: (widget, editor) => {
            editor.removeContentWidget(widget);
            widget.dispose();
        },
        widgetId: `add-comment-widget-${addToOriginalDiff ? "original" : "modified"}`
    };
}

const componentType = ":AddCommentWidget";

export function isMe(id: any): boolean {
    return typeof id === "string" && id.indexOf(componentType) > 0;
}

class AddCommentWidget extends AddBaseWidget<IAddCommentWidgetProps> implements monaco.editor.IContentWidget {
    private delay: number;

    constructor(editor: monaco.editor.ICodeEditor, props: IAddCommentWidgetProps) {
        super(editor, props);

        this.disposables.push(
            editor.onKeyDown(e => {
                if (e.keyCode === monaco.KeyCode.F4) {
                    const { originalSelection } = this;
                    if (originalSelection) {
                        if (
                            originalSelection.startLineNumber === originalSelection.endLineNumber &&
                            originalSelection.startColumn === originalSelection.endColumn
                        ) {
                            // Make sure whole line is selected if current selection is empty
                            this.addComment({
                                endColumn: this.editor.getModel()!.getLineLength(originalSelection.endLineNumber) + 1,
                                endLineNumber: originalSelection.endLineNumber,
                                startColumn: 1,
                                startLineNumber: originalSelection.startLineNumber
                            });
                        } else {
                            this.addComment(originalSelection);
                        }

                        e.preventDefault();
                    }
                }
            })
        );
    }

    public getId(): string {
        return `${this.editor.getId()}:${this.props.addToOriginalDiff ? "Original" : "Modified"}${componentType}`;
    }

    public getDomNode(): HTMLElement {
        if (!this.domNode) {
            this.domNode = document.createElement("div");

            ReactDOM.render(
                <AddCommentButton
                    className="repos-add-comment-widget"
                    onClick={e => {
                        const { selection } = this;
                        if (selection) {
                            this.addComment(selection);
                            e.preventDefault();
                        }
                    }}
                    tooltipText={Resources.AddCommentOnSelectionTooltipText}
                />,
                this.domNode
            );
        }

        return this.domNode;
    }

    public getPosition(): monaco.editor.IContentWidgetPosition {
        if (this.selection) {
            return {
                position: {
                    column: this.selection.positionColumn,
                    lineNumber: this.selection.positionLineNumber
                },
                preference: [monaco.editor.ContentWidgetPositionPreference.EXACT]
            };
        }

        return null as any;
    }

    protected setSelection(selection: monaco.Selection | undefined, originalSelection: monaco.Selection | undefined) {
        super.setSelection(selection, originalSelection);

        if (this.delay) {
            clearTimeout(this.delay);
        }

        this.domNode.hidden = true;
        if (selection) {
            this.delay = setTimeout(() => {
                this.domNode.hidden = false;
                this.editor.layoutContentWidget(this);
            }, 250);
        }
    }

    private addComment(selection: { endColumn: number; endLineNumber: number; startColumn: number; startLineNumber: number }): void {
        const { addToOriginalDiff, onAddDiscussion } = this.props;
        onAddDiscussion(
            {
                lineEnd: selection.endLineNumber,
                lineEndOffset: selection.endColumn,
                lineStart: selection.startLineNumber,
                lineStartOffset: selection.startColumn,
                type: LineInfoType.Actual
            },
            addToOriginalDiff
        );
    }
}
